import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Dog, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import bannerImage from "@/assets/banner-pet-food.jpg";
import schesirLogo from "@/assets/schesir-logo.png";
import prolifeLogo from "@/assets/prolife-logo.png";
import disugualLogo from "@/assets/disugual-logo.png";
import vincentLogo from "@/assets/vincent-logo.jpg";
import libraLogo from "@/assets/libra-logo.png";
import naturalTrainerLogo from "@/assets/natural-trainer-logo.png";
import hillsLogo from "@/assets/hills-logo.png";
import advanceLogo from "@/assets/advance-logo.png";
import stuzzyLogo from "@/assets/stuzzy-logo.jpeg";

const AlimentazioneAnimali = () => {
  const products = [
    {
      name: "Crocchette per cani",
      description: "Crocchette per cani di tutte le taglie e età, formulate per garantire un'alimentazione completa ed equilibrata",
      features: ["Puppy (cuccioli)", "Adult (adulti)", "Senior (anziani)", "Taglie small, medium, large"]
    },
    {
      name: "Cibo per gatti",
      description: "Alimenti secchi e umidi per gatti, studiati per soddisfare le loro esigenze nutrizionali specifiche",
      features: ["Cibo secco", "Cibo umido (patè e bocconcini)", "Formulazioni specifiche per età", "Prodotti per gatti sterilizzati"]
    },
    {
      name: "Snack e integratori",
      description: "Una vasta gamma di snack gustosi e integratori per il benessere dei tuoi animali",
      features: ["Snack per l'igiene dentale", "Integratori vitaminici", "Snack premium e biologici"]
    },
    {
      name: "Marchi premium",
      description: "Lavoriamo con i migliori brand del settore per garantire qualità e sicurezza",
      isPremiumBrands: true
    }
  ];

  const premiumBrands = [
    { name: "Schesir", logo: schesirLogo },
    { name: "Prolife", logo: prolifeLogo },
    { name: "Disugual", logo: disugualLogo },
    { name: "Vincent", logo: vincentLogo },
    { name: "Libra", logo: libraLogo },
    { name: "Natural Trainer", logo: naturalTrainerLogo },
    { name: "Hill's", logo: hillsLogo },
    { name: "Advance", logo: advanceLogo },
    { name: "Stuzzy", logo: stuzzyLogo }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Negozio per Animali a Magione - Crocchette Cani e Gatti"
        description="Il tuo negozio per animali tra Perugia e il Trasimeno: crocchette per cani, cibo umido e secco per gatti, snack e integratori. Prolife, Schesir, Trainer, Hill's. A Magione (PG)."
        canonical="/prodotti/alimentazione-animali"
        type="product"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Prodotti", url: "/#prodotti" },
        { name: "Alimentazione Animali", url: "/prodotti/alimentazione-animali" }
      ]} />
      <Navbar />

      {/* Hero Banner Section */}
      <section className="relative h-[450px] md:h-[500px] overflow-hidden pt-24 md:pt-28">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bannerImage})` }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        {/* Content */}
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-start md:justify-center pt-2 md:pt-0">
          <Link to="/#prodotti" className="mb-6 w-fit">
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai prodotti
            </Button>
          </Link>

          <div className="max-w-4xl">
            <div className="icon-wrapper mb-6 w-16 h-16 text-white hidden md:flex items-center justify-center">
              <Dog className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-white">
              Alimentazione Animali Domestici
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Prodotti di alta qualità per la nutrizione e il benessere dei tuoi amici a quattro zampe.
              Dai cuccioli agli anziani, per cani e gatti di tutte le taglie.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {products.map((product, index) => (
              <div
                key={index}
                className="bg-card rounded-2xl p-8 border border-border card-hover"
              >
                <h3 className="text-2xl font-heading font-semibold mb-4">
                  {product.name}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {product.description}
                </p>
                {product.isPremiumBrands ? (
                  <div className="grid grid-cols-3 gap-4">
                    {premiumBrands.map((brand, brandIndex) => (
                      <div
                        key={brandIndex}
                        className="bg-white rounded-lg p-3 border border-border flex items-center justify-center aspect-square"
                      >
                        <img
                          src={brand.logo}
                          alt={`${brand.name} crocchette e cibo per cani e gatti - Mangimi Rubini`}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {product.features?.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <span className="text-primary mr-2">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold mb-4">
            Hai bisogno di consigli?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Il nostro personale esperto è a tua disposizione per aiutarti a scegliere
            il prodotto più adatto alle esigenze del tuo animale.
          </p>
          <Link
            to="/#contatti"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Contattaci
          </Link>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default AlimentazioneAnimali;
